/**
 * firebase.ts — The only file in the game that knows Firebase exists.
 *
 * Deliberately thin. Every decision cloud save makes — what to merge, when to
 * write, what to do when a write fails — lives in `session.ts` and `save.ts`,
 * where it can be tested against `src/test/fakeBackend.ts`. What's left here is
 * glue: read the config, load the SDK, translate five calls.
 *
 * ## Two things it gets right that are easy to get wrong
 *
 * **The SDK is imported dynamically.** Firebase is larger than the rest of this
 * game put together, and most players will never sign in. `import()` keeps it
 * out of the initial bundle so it costs them nothing.
 *
 * **`firestore/lite`, not `firestore`.** The full client ships an offline cache
 * and a realtime query engine. This feature reads one document at sign-in and
 * writes one after a win, so all of that would be weight with nothing to do.
 *
 * ## The config is not a secret
 *
 * These values are compiled into every client bundle by design — that is how
 * Firebase works, and Google publishes them in its own quickstarts. What
 * protects the data is the Firestore rules in `firestore.rules`, which pin every
 * document to the uid that owns it. They live in env vars so dev and production
 * can point at different projects, not for secrecy.
 */
import type { Backend, CloudUser, Connector } from './backend';
import type { SaveData } from './save';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/** Where a player's save lives. One document, keyed by uid. */
const COLLECTION = 'saves';

/**
 * A connector for this build, or `null` if it has no Firebase config.
 *
 * `null` is the normal, supported case — a plain `git clone` with no
 * `.env.local` gets it, and so does CI. The game runs exactly as it did before
 * cloud save existed: local progress, and no sign-in button in the menu.
 */
export function firebaseConnector(): Connector | null {
  if (!config.apiKey || !config.authDomain || !config.projectId || !config.appId) return null;

  return async (): Promise<Backend> => {
    const [{ initializeApp }, auth, db] = await Promise.all([
      import('firebase/app'),
      import('firebase/auth'),
      import('firebase/firestore/lite'),
    ]);

    const app = initializeApp(config);
    const authority = auth.getAuth(app);
    const provider = new auth.GoogleAuthProvider();
    const firestore = db.getFirestore(app);
    const docFor = (uid: string): ReturnType<typeof db.doc> => db.doc(firestore, COLLECTION, uid);

    const asUser = (user: { uid: string; email: string | null; displayName: string | null } | null): CloudUser | null =>
      user ? { uid: user.uid, label: user.email ?? user.displayName ?? '' } : null;

    return {
      onUser(callback) {
        return auth.onAuthStateChanged(authority, (user) => callback(asUser(user)));
      },

      async signIn() {
        try {
          await auth.signInWithPopup(authority, provider);
        } catch (error) {
          // Popups are blocked outright on some mobile browsers and by a fair
          // few extensions. Redirect always works, so fall back to it rather
          // than telling the player their browser can't sign in. A cancelled
          // popup is the player's own choice and must not trigger this.
          if (!shouldRetryWithRedirect(error)) throw error;
          await auth.signInWithRedirect(authority, provider);
          // Navigation is under way; `onAuthStateChanged` picks the session up
          // on the way back in, which is why CloudSave records the attempt
          // before calling this.
        }
      },

      async signOut() {
        await auth.signOut(authority);
      },

      async load(uid) {
        const snapshot = await db.getDoc(docFor(uid));
        return snapshot.exists() ? snapshot.data() : null;
      },

      async store(uid: string, save: SaveData) {
        // `completed` is readonly on the way in and Firestore wants a plain
        // mutable array, so it gets copied rather than cast.
        await db.setDoc(docFor(uid), { ...save, completed: [...save.completed] });
      },
    };
  };
}

/** Is this a "popups don't work here" failure, rather than the player saying no? */
function shouldRetryWithRedirect(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code ?? '';
  return (
    code === 'auth/popup-blocked' ||
    code === 'auth/operation-not-supported-in-this-environment' ||
    code === 'auth/web-storage-unsupported'
  );
}
